import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Plus, RotateCcw } from "lucide-react";
import { NIGHT_Q, nightScore, SHAPE_READ } from "@/lib/sleepData";

const GOLD = "#EBD9A6";

export default function NightLog({ nights, onLog, onClear }) {
  const [draft, setDraft] = useState(null);
  const list = nights || [];
  const scores = list.map(nightScore);
  const avg = scores.length ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : 0;

  const path = useMemo(() => {
    if (scores.length < 2) return null;
    const step = 960 / 13;
    return scores.map((s, i) => `${(i * step).toFixed(1)} ${(160 - (s / 100) * 130).toFixed(1)}`).join(" L ");
  }, [scores]);

  const submit = () => { onLog(draft); setDraft(null); };

  return (
    <section className="relative" style={{ background: "linear-gradient(180deg, #0C1029, #151B3F)" }} data-testid="sleep-nights">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-20">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-9">
          <div>
            <p className="text-[10px] tracking-[0.24em] uppercase text-white/40">Fourteen nights</p>
            <h2 className="font-display text-[32px] sm:text-[44px] leading-[1.02] mt-2 max-w-lg text-white">Your own shape, not somebody's average</h2>
            <p className="text-[14.5px] mt-3 max-w-md text-white/50">Three answers each morning. Nothing worn, nothing measured, no graph pretending to know your sleep stages.</p>
          </div>
          <div className="text-right">
            <p className="font-display text-[54px] leading-none tabular-nums" style={{ color: GOLD }}>{list.length ? avg : "--"}</p>
            <p className="text-[11px] tracking-[0.18em] uppercase mt-1 text-white/40">{list.length} of 14 logged</p>
          </div>
        </div>

        <div className="rounded-[2rem] p-6 sm:p-8 relative overflow-hidden" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)" }}>
          <svg viewBox="0 0 960 170" className="w-full h-[150px] sm:h-[180px]" preserveAspectRatio="none">
            <defs>
              <linearGradient id="nightline" x1="0" x2="1">
                <stop offset="0%" stopColor="#6B74DC" /><stop offset="100%" stopColor={GOLD} />
              </linearGradient>
              <linearGradient id="nightfill" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="rgba(235,217,166,0.28)" /><stop offset="100%" stopColor="rgba(107,116,220,0)" />
              </linearGradient>
            </defs>
            {[30, 70, 110, 150].map((y) => <line key={y} x1="0" x2="960" y1={y} y2={y} stroke="rgba(255,255,255,0.06)" />)}
            {path && <motion.path d={`M ${path} L 960 170 L 0 170 Z`} fill="url(#nightfill)" initial={{ opacity: 0 }} animate={{ opacity: 1 }} />}
            {path && <motion.path d={`M ${path}`} fill="none" stroke="url(#nightline)" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"
              initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }} />}
            {scores.map((s, i) => (
              <circle key={i} cx={(i * 960) / 13} cy={160 - (s / 100) * 130} r="5" fill={GOLD} />
            ))}
          </svg>

          <div className="flex gap-1.5 mt-5">
            {Array.from({ length: 14 }, (_, i) => (
              <span key={i} className="flex-1 h-1.5 rounded-full" style={{ background: i < list.length ? GOLD : "rgba(255,255,255,0.1)" }} />
            ))}
          </div>

          <p className="text-[13.5px] mt-6 max-w-2xl text-white/55">{SHAPE_READ(avg, list.length)}</p>

          <div className="mt-7 flex flex-wrap items-center gap-3">
            {!draft ? (
              <button onClick={() => setDraft({})} disabled={list.length >= 14} className="inline-flex items-center gap-2.5 rounded-full px-6 py-3.5 text-sm font-semibold disabled:opacity-40"
                style={{ background: GOLD, color: "#171B3C" }} data-testid="night-log-open">
                <Plus size={14} /> {list.length ? "Log last night" : "Log your first night"}
              </button>
            ) : null}
            {list.length > 0 && (
              <button onClick={onClear} className="text-[11.5px] inline-flex items-center gap-1.5 text-white/35" data-testid="night-log-clear">
                <RotateCcw size={10} /> start the fortnight again
              </button>
            )}
          </div>

          <AnimatePresence>
            {draft && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {NIGHT_Q.map((q) => (
                    <div key={q.k} data-testid={`night-q-${q.k}`}>
                      <p className="text-[12.5px] text-white/60">{q.label}</p>
                      <div className="mt-2.5 space-y-1.5">
                        {q.options.map((o, n) => {
                          const on = draft[q.k] === n;
                          return (
                            <button key={o} onClick={() => setDraft({ ...draft, [q.k]: n })} className="w-full text-left rounded-xl px-3.5 py-2.5 text-[13px] transition-all hover:translate-x-1"
                              style={on ? { background: "rgba(235,217,166,0.18)", color: "#fff", border: `1px solid ${GOLD}` } : { background: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.6)", border: "1px solid rgba(255,255,255,0.07)" }}
                              data-testid={`night-a-${q.k}-${n}`}>{o}</button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-3 mt-6">
                  <button onClick={submit} disabled={NIGHT_Q.some((q) => draft[q.k] === undefined)}
                    className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold disabled:opacity-35"
                    style={{ background: GOLD, color: "#171B3C" }} data-testid="night-log-save">
                    <Check size={14} /> Add this night
                  </button>
                  <button onClick={() => setDraft(null)} className="text-[12px] text-white/40" data-testid="night-log-cancel">not now</button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
